import { computed, watch, type WritableComputedRef } from 'vue';
import { toast } from '@beonit/adaptive-raindrop';
import { feedbackFor } from '../feedback.js';
import { metricsReading } from '../lib/metrics-reading.js';
import type { ScoreMethod } from '../lib/metrics-series.js';
import { usePreferences, useUpdatePreferences } from '../queries.js';

/**
 * The preferred score method (product owner, 2026-10-06): «Alerta mayor» or «Resta calculada»,
 * chosen once in the hub's options and in force on every screen that shows figures, kept per user
 * on the server like «Métricas globales».
 *
 * Reading it anywhere keeps `metricsReading.method` in step with the server; setting it changes
 * every screen at once and saves it. The module's navigation reads it on every screen, so the
 * choice is in force before any figure is painted.
 */
export function usePreferredMethod(): WritableComputedRef<ScoreMethod> {
  const { data } = usePreferences();
  const update = useUpdatePreferences();

  watch(
    () => data.value?.scoreMethod,
    (value) => {
      if (value !== undefined) metricsReading.method = value;
    },
    { immediate: true },
  );

  return computed({
    get: () => data.value?.scoreMethod ?? metricsReading.method,
    set: (value: ScoreMethod) => {
      metricsReading.method = value;
      update.mutate(
        { scoreMethod: value },
        { onError: (error) => toast.error(feedbackFor(error)) },
      );
    },
  });
}
